import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, CalendarCheck, Clock, Plus, ChevronRight, BarChart3, Flame, Activity, TrendingUp, Ban, AlertTriangle, Phone, Mail } from 'lucide-react';
import { fmtDateShort } from '../utils/dates';
import { sportsApi } from '../SportsAuthContext';
import { useSportsAuth } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import PlayerChampions from '../components/PlayerChampions';
import { fmt12 } from '../utils/sportsUtils';

/* ── Small pure-CSS chart helpers ─────────────────────────────── */

function RevenueChart({ data, labelKey, valueKey }) {
  const max = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div className="flex items-end gap-1.5 h-40" data-testid="sports-analytics-revenue-chart">
      {data.map((d, i) => {
        const pct = (d[valueKey] / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 rounded text-xs font-bold whitespace-nowrap pointer-events-none"
              style={{ background: '#E63946', color: '#fff', fontSize: 10 }}>
              ₱{d[valueKey].toLocaleString()}
            </div>
            <div className="w-full rounded-t transition-all duration-300 group-hover:opacity-80"
              style={{
                height: `${Math.max(pct, 2)}%`,
                background: d[valueKey] > 0 ? 'linear-gradient(to top, #E63946, #FF6B6B)' : '#1E1E1E',
                minHeight: 3,
              }} />
            <span className="text-[9px] text-center leading-tight" style={{ color: '#555' }}>
              {d[labelKey]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Heatmap({ data }) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 18 }, (_, i) => i + 5); // 5AM – 10PM
  const flat = data.flat();
  const maxVal = Math.max(...flat, 1);

  const getColor = (v) => {
    if (v === 0) return '#111';
    const intensity = v / maxVal;
    if (intensity < 0.25) return '#3B1219';
    if (intensity < 0.5) return '#7F1D2F';
    if (intensity < 0.75) return '#C2293E';
    return '#E63946';
  };

  return (
    <div className="overflow-x-auto" data-testid="sports-analytics-heatmap">
      <div className="min-w-[500px]">
        {/* Hour labels */}
        <div className="flex gap-px ml-9 mb-1">
          {hours.map(h => (
            <div key={h} className="flex-1 text-center text-[8px]" style={{ color: '#555' }}>
              {h % 3 === 0 ? `${h > 12 ? h - 12 : h}${h >= 12 ? 'p' : 'a'}` : ''}
            </div>
          ))}
        </div>
        {/* Grid */}
        {days.map((day, di) => (
          <div key={day} className="flex items-center gap-px mb-px">
            <span className="w-8 text-[10px] text-right pr-1 shrink-0" style={{ color: '#555' }}>{day}</span>
            {hours.map(h => {
              const val = (data[di] && data[di][h]) || 0;
              return (
                <div key={h} className="flex-1 aspect-square rounded-sm group relative cursor-default"
                  style={{ background: getColor(val), minHeight: 14 }}>
                  {val > 0 && (
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap z-10 pointer-events-none"
                      style={{ background: '#fff', color: '#0A0A0A' }}>
                      {val} booking{val > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function UtilizationBars({ courts }) {
  return (
    <div className="space-y-3" data-testid="sports-analytics-utilization">
      {courts.map(c => (
        <div key={c.court_id}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold" style={{ color: '#ccc' }}>{c.court_name}</span>
            <span className="text-xs font-bold" style={{ color: c.utilization_pct > 60 ? '#00E676' : c.utilization_pct > 30 ? '#FFB300' : '#737373' }}>
              {c.utilization_pct}%
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: '#1E1E1E' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.max(c.utilization_pct, 1)}%`,
                background: c.utilization_pct > 60 ? '#00E676' : c.utilization_pct > 30 ? '#FFB300' : '#E63946',
              }} />
          </div>
          <p className="text-[10px] mt-0.5" style={{ color: '#555' }}>
            {c.booked_hours}h booked / {c.available_hours}h available (30 days)
          </p>
        </div>
      ))}
      {courts.length === 0 && <p className="text-xs" style={{ color: '#555' }}>No active courts.</p>}
    </div>
  );
}

/* ── Main Dashboard ───────────────────────────────────────────── */

export default function SportsDashboardPage() {
  const { organization } = useSportsAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [revView, setRevView] = useState('weekly'); // 'weekly' | 'monthly'
  const [billing, setBilling] = useState(null);
  const [closingDay, setClosingDay] = useState(false);
  const [dayClose, setDayClose] = useState(null); // close-day summary result

  const handleCloseDay = async () => {
    if (closingDay) return;
    setClosingDay(true);
    try {
      const r = await sportsApi.post('/sports/reports/close-day', {});
      setDayClose(r.data);
    } catch (e) {
      setDayClose({ error: e.response?.data?.detail || 'Could not build the summary' });
    } finally {
      setClosingDay(false);
    }
  };

  useEffect(() => {
    sportsApi.get('/sports/dashboard/today')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    sportsApi.get('/sports/dashboard/analytics')
      .then(r => setAnalytics(r.data))
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false));
    sportsApi.get('/sports/billing/status')
      .then(r => setBilling(r.data))
      .catch(() => {});
  }, []);

  const pending = stats?.pending_confirmations || 0;

  // Trial warning — from org context
  const trialDaysRemaining = organization?.trial_days_remaining;
  const isTrialExpired = organization?.account_status === 'trial' && typeof trialDaysRemaining === 'number' && trialDaysRemaining < 0;
  const isTrialExpiringSoon = organization?.account_status === 'trial' && typeof trialDaysRemaining === 'number' && trialDaysRemaining >= 0 && trialDaysRemaining <= 3;
  const showTrialBanner = isTrialExpired || isTrialExpiringSoon;
  // Active subscription nearing renewal (or lapsed) — owner-side warning only
  // 7-day window matches the first reminder email, so the banner and the inbox agree.
  const showRenewalBanner = !showTrialBanner && billing && !billing.is_free && !billing.in_trial
    && typeof billing.days_left === 'number' && billing.days_left <= 7;
  const renewalExpired = showRenewalBanner && billing.days_left < 0;

  const STAT_CARDS = [
    {
      label: "Today's Income",
      value: stats ? `₱${(stats.income_paid_today || 0).toLocaleString()}` : '—',
      icon: DollarSign, color: '#00E676', testid: 'sports-stat-income',
    },
    {
      label: "Bookings Today",
      value: stats ? String(stats.bookings_today || 0) : '—',
      icon: CalendarCheck, color: '#2979FF', testid: 'sports-stat-bookings',
    },
    {
      label: "Pending Review",
      value: stats ? String(pending) : '—',
      icon: Clock, color: pending > 0 ? '#FF2D55' : '#FFB300', testid: 'sports-stat-pending',
      urgent: pending > 0,
      action: pending > 0 ? () => navigate('/sports/bookings') : null,
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar pendingCount={pending} />

      <main className="max-w-6xl mx-auto px-5 py-10">
        {/* Trial Warning Banner */}
        {showTrialBanner && (
          <div data-testid="trial-warning-banner"
            className="mb-6 rounded-xl p-4 flex items-start gap-3"
            style={{ background: isTrialExpired ? '#2D0A0A' : '#2D1A00', border: `1px solid ${isTrialExpired ? '#E63946' : '#FFB300'}` }}>
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: isTrialExpired ? '#E63946' : '#FFB300' }} />
            <div className="flex-1 min-w-0">
              {isTrialExpired ? (
                <>
                  <p className="text-sm font-bold" style={{ color: '#E63946' }}>Trial Period Ended</p>
                  <p className="text-xs mt-0.5" style={{ color: '#A3A3A3' }}>
                    Your facility is no longer accepting new bookings. Existing bookings are not affected.
                    Renew now to reactivate instantly.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold" style={{ color: '#FFB300' }}>
                    Trial ends in {trialDaysRemaining === 0 ? 'less than 24 hours' : `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'}`}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: '#A3A3A3' }}>
                    New bookings will stop being accepted when the trial expires. Renew now to avoid any interruption.
                  </p>
                </>
              )}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <button onClick={() => navigate('/sports/billing')} data-testid="trial-renew-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                  style={{ background: isTrialExpired ? '#E63946' : '#FFB300', color: isTrialExpired ? '#fff' : '#0A0A0A', border: 'none', cursor: 'pointer' }}>
                  Subscribe / Renew Now
                </button>
                <a href="tel:+639952568450" data-testid="trial-call-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                  style={{ background: 'transparent', color: '#A3A3A3', border: '1px solid #333' }}>
                  <Phone className="h-3.5 w-3.5" /> Call 0995 256 8450
                </a>
              </div>
            </div>
          </div>
        )}
        {/* Subscription renewal banner (active tenants — owner-side only) */}
        {showRenewalBanner && (
          <div data-testid="renewal-warning-banner"
            className="mb-6 rounded-xl p-4 flex items-start gap-3"
            style={{ background: renewalExpired ? '#2D0A0A' : '#2D1A00', border: `1px solid ${renewalExpired ? '#E63946' : '#FFB300'}` }}>
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: renewalExpired ? '#E63946' : '#FFB300' }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold" style={{ color: renewalExpired ? '#E63946' : '#FFB300' }}>
                {renewalExpired ? 'Subscription Expired' : `Subscription renews in ${billing.days_left === 0 ? 'less than 24 hours' : `${billing.days_left} day${billing.days_left === 1 ? '' : 's'}`}`}
              </p>
              <p className="text-xs mt-0.5" style={{ color: '#A3A3A3' }}>
                {renewalExpired
                  ? 'Your public page has stopped accepting new bookings. Renew to reactivate instantly.'
                  : `₱${Number(billing.amount_due_php || 0).toLocaleString()} due — renew now to avoid any interruption to online bookings.`}
              </p>
              <button onClick={() => navigate('/sports/billing')} data-testid="renewal-banner-btn"
                className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ background: renewalExpired ? '#E63946' : '#FFB300', color: renewalExpired ? '#fff' : '#0A0A0A', border: 'none', cursor: 'pointer' }}>
                Renew Now — ₱{Number(billing.amount_due_php || 0).toLocaleString()}
              </button>
            </div>
          </div>
        )}
        {/* Welcome */}
        <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#737373' }}>Welcome back</p>
        <h1 className="text-4xl sm:text-5xl uppercase tracking-tight mb-1"
          style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>
          {organization?.name || 'Your Facility'}
        </h1>

        {/* Today stats */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {STAT_CARDS.map(c => (
            <div key={c.label} data-testid={c.testid}
              onClick={c.action || undefined}
              className="rounded-xl p-5 transition-all"
              style={{
                background: '#141414', border: `1px solid ${c.urgent ? c.color + '40' : '#1E1E1E'}`,
                cursor: c.action ? 'pointer' : 'default',
                boxShadow: c.urgent ? `0 0 20px ${c.color}18` : 'none',
              }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider" style={{ color: '#737373' }}>{c.label}</span>
                <span className="h-8 w-8 rounded-lg flex items-center justify-center"
                  style={{ background: c.color + '1A', color: c.color }}>
                  <c.icon className="h-4 w-4" />
                </span>
              </div>
              {loading
                ? <div className="h-8 w-16 rounded animate-pulse" style={{ background: '#2A2A2A' }} />
                : <p className="text-3xl font-bold" style={{ color: c.color }}>{c.value}</p>
              }
              {c.urgent && <p className="text-xs mt-2" style={{ color: c.color }}>Tap to review →</p>}
            </div>
          ))}
        </div>

        {/* Close the day — money summary + owner email */}
        <div className="mt-4 rounded-xl p-4" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>Close the Day</p>
              <p className="text-xs mt-0.5" style={{ color: '#737373' }}>
                Count the drawer: booked vs collected vs still owed — emailed to you with the full breakdown.
              </p>
            </div>
            <button onClick={handleCloseDay} disabled={closingDay} data-testid="sports-close-day-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase"
              style={{ background: '#00E67618', color: '#00E676', border: '1px solid #00E67640', cursor: closingDay ? 'wait' : 'pointer' }}>
              <Mail className="h-3.5 w-3.5" /> {closingDay ? 'Counting…' : 'Close the Day'}
            </button>
          </div>
          {dayClose && !dayClose.error && (
            <div className="mt-3 grid grid-cols-3 gap-3" data-testid="sports-close-day-result">
              {[
                { label: 'Booked value', v: dayClose.summary?.booked_value, color: '#fff' },
                { label: 'Collected', v: dayClose.summary?.collected_today, color: '#00E676' },
                { label: 'Still owed', v: dayClose.summary?.outstanding_total, color: '#FF2D55' },
              ].map(x => (
                <div key={x.label} className="rounded-lg p-3" style={{ background: '#0A0A0A' }}>
                  <p className="text-[10px] uppercase" style={{ color: '#555' }}>{x.label}</p>
                  <p className="text-lg font-bold" style={{ color: x.color }}>₱{Number(x.v || 0).toLocaleString()}</p>
                </div>
              ))}
              <p className="col-span-3 text-xs" style={{ color: dayClose.email_sent ? '#00E676' : '#737373' }}>
                {dayClose.email_sent ? 'Summary emailed to the owner.' : 'Email not sent (no email key configured) — summary shown here.'}
              </p>
            </div>
          )}
          {dayClose?.error && <p className="mt-2 text-xs" style={{ color: '#FF2D55' }}>{String(dayClose.error)}</p>}
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-sm uppercase tracking-wider mb-4" style={{ color: '#737373', fontFamily: 'Oswald, sans-serif' }}>Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'Open a Court', desc: 'Add a new court — set sport, hours & rates.', to: '/sports/courts/new', color: '#E63946', testid: 'sports-quick-add-court' },
              { label: 'View Bookings', desc: 'Confirm or decline customer booking requests.', to: '/sports/bookings', color: '#2979FF', testid: 'sports-quick-bookings', badge: pending > 0 ? pending : 0 },
              { label: 'Court Calendar', desc: 'See which slots are open or taken today.', to: '/sports/monitoring', color: '#FFB300', testid: 'sports-quick-calendar' },
              { label: 'Setup My Page', desc: 'Customize your public booking site theme & GCash.', to: '/sports/settings', color: '#00E676', testid: 'sports-quick-settings' },
            ].map(a => (
              <button key={a.label} data-testid={a.testid}
                onClick={() => navigate(a.to)}
                className="text-left rounded-xl p-5 flex items-center justify-between transition-all group"
                style={{ background: '#141414', border: '1px solid #1E1E1E' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = a.color + '50'; e.currentTarget.style.background = a.color + '08'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1E1E1E'; e.currentTarget.style.background = '#141414'; }}>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold uppercase tracking-tight"
                      style={{ fontFamily: 'Oswald, sans-serif', color: a.color }}>{a.label}</span>
                    {a.badge > 0 && (
                      <span className="h-5 px-1.5 rounded-full text-white text-xs flex items-center justify-center"
                        style={{ background: '#E63946', fontSize: 10 }}>{a.badge}</span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: '#737373' }}>{a.desc}</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0" style={{ color: '#737373' }} />
              </button>
            ))}
          </div>
        </div>

        {/* Upcoming bookings */}
        {stats?.upcoming?.length > 0 && (
          <div className="mt-8">
            <h2 className="text-sm uppercase tracking-wider mb-4" style={{ color: '#737373', fontFamily: 'Oswald, sans-serif' }}>Upcoming Confirmed</h2>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #1E1E1E' }}>
              {stats.upcoming.slice(0, 5).map((b, i) => (
                <div key={b.id || i} data-testid={`sports-upcoming-${i}`}
                  className="flex items-center justify-between px-5 py-3"
                  style={{ borderBottom: i < stats.upcoming.length - 1 ? '1px solid #1E1E1E' : 'none', background: '#141414' }}>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#fff' }}>{b.customer?.name || '—'}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#737373' }}>{b.court_name} · {fmtDateShort(b.date)} · {fmt12(b.start_time)}–{fmt12(b.end_time)}</p>
                  </div>
                  <span className="text-sm font-bold" style={{ color: '#00E676' }}>₱{(b.amount || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Analytics Section ──────────────────────────────────── */}
        <div className="mt-10 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="h-4 w-4" style={{ color: '#E63946' }} />
            <h2 className="text-sm uppercase tracking-wider" style={{ color: '#737373', fontFamily: 'Oswald, sans-serif' }}>Analytics</h2>
          </div>
          <p className="text-xs" style={{ color: '#444' }}>Last 30 days performance overview</p>
        </div>

        {/* 30-day summary cards */}
        {analytics?.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="sports-analytics-summary">
            {[
              { label: '30-Day Revenue', value: `₱${analytics.summary.total_revenue_30d.toLocaleString()}`, icon: TrendingUp, color: '#00E676' },
              { label: '30-Day Bookings', value: String(analytics.summary.total_bookings_30d), icon: CalendarCheck, color: '#2979FF' },
              { label: 'Avg Booking Value', value: `₱${analytics.summary.avg_booking_value.toLocaleString()}`, icon: DollarSign, color: '#FFB300' },
              { label: 'Cancelled (30d)', value: String(analytics.summary.cancelled_30d), icon: Ban, color: '#737373' },
            ].map(s => (
              <div key={s.label} className="rounded-xl p-4" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
                <div className="flex items-center gap-1.5 mb-2">
                  <s.icon className="h-3 w-3" style={{ color: s.color }} />
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: '#555' }}>{s.label}</span>
                </div>
                <p className="text-xl font-bold" style={{ color: s.color, fontFamily: 'Oswald, sans-serif' }}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {analyticsLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-52 rounded-xl animate-pulse" style={{ background: '#141414' }} />)}
          </div>
        ) : analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Revenue Chart */}
            <div className="rounded-xl p-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs uppercase tracking-wider font-bold" style={{ color: '#A3A3A3', fontFamily: 'Oswald, sans-serif' }}>Revenue</h3>
                <div className="flex gap-1 p-0.5 rounded-md" style={{ background: '#0A0A0A' }}>
                  {['weekly', 'monthly'].map(v => (
                    <button key={v} onClick={() => setRevView(v)}
                      data-testid={`sports-analytics-rev-${v}`}
                      className="px-2.5 py-1 rounded text-[10px] font-semibold uppercase transition-colors"
                      style={{
                        background: revView === v ? '#E63946' : 'transparent',
                        color: revView === v ? '#fff' : '#555',
                      }}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <RevenueChart
                data={revView === 'weekly' ? analytics.weekly_revenue : analytics.monthly_revenue}
                labelKey={revView === 'weekly' ? 'week' : 'month'}
                valueKey="revenue"
              />
            </div>

            {/* Court Utilization */}
            <div className="rounded-xl p-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
              <div className="flex items-center gap-2 mb-4">
                <Activity className="h-3.5 w-3.5" style={{ color: '#E63946' }} />
                <h3 className="text-xs uppercase tracking-wider font-bold" style={{ color: '#A3A3A3', fontFamily: 'Oswald, sans-serif' }}>Court Utilization (30d)</h3>
              </div>
              <UtilizationBars courts={analytics.court_utilization} />
            </div>

            {/* Peak Hours Heatmap — full width */}
            <div className="rounded-xl p-5 lg:col-span-2" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
              <div className="flex items-center gap-2 mb-4">
                <Flame className="h-3.5 w-3.5" style={{ color: '#E63946' }} />
                <h3 className="text-xs uppercase tracking-wider font-bold" style={{ color: '#A3A3A3', fontFamily: 'Oswald, sans-serif' }}>Peak Booking Hours</h3>
                <span className="text-[10px] ml-auto" style={{ color: '#444' }}>All-time data · Hover cells for counts</span>
              </div>
              <Heatmap data={analytics.heatmap} />
              {/* Legend */}
              <div className="flex items-center gap-2 mt-3 justify-end">
                <span className="text-[9px]" style={{ color: '#555' }}>Less</span>
                {['#111', '#3B1219', '#7F1D2F', '#C2293E', '#E63946'].map(c => (
                  <div key={c} className="w-3 h-3 rounded-sm" style={{ background: c }} />
                ))}
                <span className="text-[9px]" style={{ color: '#555' }}>More</span>
              </div>
            </div>
          </div>
        )}

        {/* iter516 — Player Champions widget (Competitive Mode analytics) */}
        <div className="mt-6">
          <PlayerChampions />
        </div>

        {/* Add first court CTA */}
        {!loading && !stats?.bookings_today && (
          <div className="mt-8 rounded-xl p-8 text-center" style={{ border: '1px dashed #2A2A2A', background: '#0D0D0D' }}>
            <p className="text-sm mb-3" style={{ color: '#737373' }}>No courts yet? Add your first court to start accepting bookings.</p>
            <button data-testid="sports-cta-add-court"
              onClick={() => navigate('/sports/courts/new')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-bold uppercase tracking-wide transition-all"
              style={{ background: '#E63946', color: '#fff', boxShadow: '0 0 20px rgba(230,57,70,0.35)' }}>
              <Plus className="h-4 w-4" /> Open a Court
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
